import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SelectPopover } from '../../../../components/SelectPopover';
import {
  SCHOLARSHIP_PLAYER_POSITIONS,
  type LineupSlot,
  type LineupVersus,
  type RosterSettings,
  type ScholarshipPlayer,
  type ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import { tourAnchor } from '../../../tours/tourAnchors';
import { restrictToVerticalAxis } from './dragModifiers';
import { DragHandleIcon } from './icons';
import {
  addToLineup,
  benchFor,
  defaultFieldPosition,
  LINEUP_MAX,
  LINEUP_TITLES,
  moveInLineup,
  removeFromLineup,
  setFieldPosition,
} from './lineups';
import { positionLabel, type RosterSport } from './positionGroups';
import { HandednessName, TraitPill } from './RosterPlayerMarks';
import { compactClassLabel } from './scholarshipClasses';

interface Props {
  side: LineupVersus;
  slots: LineupSlot[];
  players: ScholarshipPlayer[];
  playersById: ReadonlyMap<string, ScholarshipPlayer>;
  settings: RosterSettings;
  sport: RosterSport;
  /** The whole next lineup for this side; the tab saves it. */
  onChange: (side: LineupVersus, next: LineupSlot[]) => void;
  /** The first card carries the drag and bench tour anchors (each card carries its own lhp/rhp). */
  ownsRowAnchors: boolean;
}

const battingSide = (p: ScholarshipPlayer): string =>
  `${p.bats ?? '—'}/${p.throws ?? '—'}`;

interface RowProps {
  slot: LineupSlot;
  player: ScholarshipPlayer;
  side: LineupVersus;
  settings: RosterSettings;
  sport: RosterSport;
  draggable: boolean;
  dragAnchor: boolean;
  onFieldPosition: (player: ScholarshipPlayer, position: ScholarshipPlayerPosition | null) => void;
  onRemove: (player: ScholarshipPlayer, index: number) => void;
  index: number;
}

const LineupRow = ({
  slot,
  player,
  side,
  settings,
  sport,
  draggable,
  dragAnchor,
  onFieldPosition,
  onRemove,
  index,
}: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: player.id,
    disabled: !draggable,
  });
  const name = `${player.firstName} ${player.lastName}`;
  const posId = `lineup-${side}-pos-${player.id}`;
  const fielding = slot.fieldPosition ?? defaultFieldPosition(player);
  // Labels, not raw values, so a softball board offers DP; mapped back on change.
  const labelFor = (p: ScholarshipPlayerPosition) => positionLabel(p, sport);
  const options = SCHOLARSHIP_PLAYER_POSITIONS.map(labelFor);

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
    >
      <td className="lineup-table__handle-cell">
        {draggable && (
          <button
            type="button"
            className="roster-drag-handle"
            aria-label={`Drag to reorder ${name}, batting ${slot.battingOrder}`}
            {...(dragAnchor ? tourAnchor('rosters.lineups.drag') : {})}
            {...attributes}
            {...listeners}
          >
            <DragHandleIcon />
          </button>
        )}
      </td>
      <td className="lineup-table__order">{slot.battingOrder}</td>
      <td>
        <HandednessName name={name} bats={player.bats} settings={settings} />
      </td>
      <td className="lineup-table__pos">
        <label htmlFor={posId} className="roster-sr-only">
          Field position for {name}
        </label>
        <SelectPopover
          id={posId}
          options={options}
          value={fielding ? labelFor(fielding) : ''}
          onChange={(label) => {
            const next = SCHOLARSHIP_PLAYER_POSITIONS.find((p) => labelFor(p) === label) ?? null;
            onFieldPosition(player, next);
          }}
          placeholder="—"
          clearable={false}
          testId={`lineup-${side}-pos-${player.id}`}
        />
      </td>
      <td>{battingSide(player)}</td>
      <td>{compactClassLabel(player) ?? '—'}</td>
      <td>
        <TraitPill traitId={player.evaluationTraitId} settings={settings} />
      </td>
      <td className="lineup-table__actions">
        <button
          type="button"
          className="roster-link-button"
          data-lineup-focus="bench"
          onClick={() => onRemove(player, index)}
          aria-label={`Move ${name} to the bench (${LINEUP_TITLES[side]})`}
        >
          Bench
        </button>
      </td>
    </tr>
  );
};

/**
 * One batting order: the nine in order, then the bench. Every change hands the whole next lineup
 * to the tab, which saves it; the card holds no copy of its own beyond whether the bench is open.
 */
export const LineupCard = ({
  side,
  slots,
  players,
  playersById,
  settings,
  sport,
  onChange,
  ownsRowAnchors,
}: Props) => {
  const [benchOpen, setBenchOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const benchToggleRef = useRef<HTMLButtonElement>(null);
  /*
    Bench and Add unmount the very button that was pressed, which drops keyboard focus to the page.
    After the next render focus goes to the same kind of button at the same place (the next hitter
    has moved up into it), or the bench toggle when there is none, and a polite status line says
    what happened, since the row a screen reader was on has gone.
  */
  const focusAfter = useRef<{ kind: 'bench' | 'add'; index: number } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const rows = slots.flatMap((slot) => {
    const player = playersById.get(slot.playerId);
    return player ? [{ slot, player }] : [];
  });
  const bench = benchFor(players, slots);
  const full = rows.length >= LINEUP_MAX;
  const draggable = rows.length > 1;
  const title = LINEUP_TITLES[side];
  const headingId = `lineup-${side}-heading`;
  const benchId = `lineup-${side}-bench`;

  useEffect(() => {
    const target = focusAfter.current;
    if (!target || !sectionRef.current) return;
    focusAfter.current = null;
    const candidates = Array.from(
      sectionRef.current.querySelectorAll<HTMLButtonElement>(`[data-lineup-focus="${target.kind}"]`),
    ).filter((button) => !button.disabled);
    const next = candidates[Math.min(target.index, candidates.length - 1)];
    (next ?? benchToggleRef.current)?.focus();
  }, [slots]);

  const benchPlayer = (player: ScholarshipPlayer, index: number) => {
    focusAfter.current = { kind: 'bench', index };
    setAnnouncement(`${player.firstName} ${player.lastName} moved to the bench ${title}.`);
    onChange(side, removeFromLineup(slots, player.id));
  };

  const addPlayer = (player: ScholarshipPlayer, index: number) => {
    focusAfter.current = { kind: 'add', index };
    const next = addToLineup(slots, player.id);
    setAnnouncement(
      `${player.firstName} ${player.lastName} added to the lineup ${title}, batting ${next.length}.`,
    );
    onChange(side, next);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onChange(side, moveInLineup(slots, String(active.id), String(over.id)));
  };

  return (
    <section
      ref={sectionRef}
      className="lineup-card"
      aria-labelledby={headingId}
      {...tourAnchor(side === 'lhp' ? 'rosters.lineups.lhp' : 'rosters.lineups.rhp')}
    >
      <h2 id={headingId} className="lineup-card__title">
        {title}
      </h2>
      <p className="roster-sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      {rows.length === 0 ? (
        <p className="lineup-card__empty">
          No batting order yet. Open the bench below and add hitters.
        </p>
      ) : (
        <div className="lineup-card__scroll">
          <DndContext
            id={`lineup-${side}`}
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={rows.map((r) => r.player.id)}
              strategy={verticalListSortingStrategy}
            >
              <table className="roster-table lineup-table">
                <caption className="roster-sr-only">Batting order {title}</caption>
                <thead>
                  <tr>
                    <th aria-label="Reorder" />
                    <th>Order</th>
                    <th>Player</th>
                    <th>Pos</th>
                    <th>B/T</th>
                    <th>Year</th>
                    <th>Type</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ slot, player }, index) => (
                    <LineupRow
                      key={player.id}
                      slot={slot}
                      player={player}
                      side={side}
                      settings={settings}
                      sport={sport}
                      draggable={draggable}
                      dragAnchor={ownsRowAnchors && index === 0}
                      onFieldPosition={(p, position) =>
                        onChange(side, setFieldPosition(slots, p.id, position, defaultFieldPosition(p)))
                      }
                      onRemove={benchPlayer}
                      index={index}
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className="lineup-bench">
        <button
          ref={benchToggleRef}
          type="button"
          className="lineup-bench__toggle"
          aria-expanded={benchOpen}
          aria-controls={benchId}
          onClick={() => setBenchOpen((open) => !open)}
          {...(ownsRowAnchors ? tourAnchor('rosters.lineups.bench') : {})}
        >
          <span>Bench ({bench.length})</span>
          <svg
            className={`lineup-bench__chevron${benchOpen ? ' lineup-bench__chevron--open' : ''}`}
            width="11"
            height="11"
            viewBox="0 0 11 11"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 3.5l3.5 3.5 3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div id={benchId} hidden={!benchOpen}>
          {bench.length === 0 ? (
            <p className="lineup-bench__empty">Every hitter on the board is in this lineup.</p>
          ) : (
            <ul className="lineup-bench__list">
              {full && (
                <li className="lineup-bench__full">
                  The order is full. Move someone to the bench to add another hitter.
                </li>
              )}
              {bench.map((player, benchIndex) => {
                const name = `${player.firstName} ${player.lastName}`;
                return (
                  <li key={player.id} className="lineup-bench__item">
                    <HandednessName name={name} bats={player.bats} settings={settings} />
                    <span className="lineup-bench__meta">
                      {player.position ? positionLabel(player.position, sport) : '—'} ·{' '}
                      {battingSide(player)} · {compactClassLabel(player) ?? '—'}
                    </span>
                    <TraitPill traitId={player.evaluationTraitId} settings={settings} />
                    <button
                      type="button"
                      className="roster-link-button lineup-bench__add"
                      data-lineup-focus="add"
                      disabled={full}
                      onClick={() => addPlayer(player, benchIndex)}
                      aria-label={`Add ${name} to the lineup ${title}`}
                    >
                      Add
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};
